import { Box, CircularProgress, Typography } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { useGetContactById } from '../../../../../services/react-query/contacts';
import { useGetAllEvents } from '../../../../../services/react-query/events';
import { Event } from '../../../../../types/Event';
import StatusPill from '../../../../../uikit/pills/StatusPill';
import { humanizeEventDateTimes } from '../../../../../utility/dateUtils';
import EmailLink from '../../../EmailLink/EmailLink';
import PhoneLink from '../../../PhoneLink/PhoneLink';
import DrawerSection from '../DrawerSection';
import InfoRow from '../InfoRow';
import ModuleDrawerHeader from '../ModuleDrawerHeader';
import { moduleDrawerStyles } from '../moduleDrawerStyles';
import CustomFieldsDisplay from './CustomFieldsDisplay';

const CenterSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
    <CircularProgress size={20} />
  </Box>
);

const ContactDrawerView: React.FC<{ uuid: string }> = ({ uuid }) => {
  const { data: contact, isLoading } = useGetContactById({ uuid });

  const { data: eventsResp, isLoading: eventsLoading } = useGetAllEvents({
    params: {
      per_page: 5,
      sort: { start_at: 1 as const },
      filters: { contacts: [uuid] },
    },
    enabled: !!uuid,
  });

  if (isLoading) return <CenterSpinner />;
  if (!contact) {
    return (
      <Typography variant="body2" color="error">
        Contact not found
      </Typography>
    );
  }

  const events = eventsResp?.data || [];

  return (
    <>
      <ModuleDrawerHeader>{_.truncate(contact.full_name)}</ModuleDrawerHeader>
      <Box sx={moduleDrawerStyles.content}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Contact Information */}
          <DrawerSection title="Contact Information">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <InfoRow label="Account" value={contact.account_name} />
              <InfoRow label="First Name" value={contact.first_name} />
              <InfoRow label="Last Name" value={contact.last_name} />
              <InfoRow label="Title" value={contact.contact_title_value} />
              <InfoRow label="Role" value={contact.roles_values?.join(', ')} />
              <InfoRow label="Email" value={<EmailLink email={contact.email} />} />
              <InfoRow label="Phone" value={<PhoneLink phone={contact.phone_1} extension={contact.extension} />} />
              <InfoRow label="Mobile" value={<PhoneLink phone={contact.phone_2} />} />
            </Box>
          </DrawerSection>

          {/* Custom Fields */}
          <CustomFieldsDisplay fields={contact.fields} />

          {/* Events */}
          <DrawerSection title="Events">
            {eventsLoading ? (
              <CenterSpinner />
            ) : events.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No events found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {_.map(events, (event: Event) => (
                  <Box key={event.uuid} sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }} title={event.display_name}>
                      {event.display_name}
                    </Typography>
                    <InfoRow label="Date / Time" value={humanizeEventDateTimes(event)} />
                    <InfoRow label="Status" value={event.status || '—'} rightNode={<StatusPill status={event.status} />} />
                  </Box>
                ))}
              </Box>
            )}
          </DrawerSection>
        </Box>
      </Box>
    </>
  );
};

export default ContactDrawerView;
